/* ===========================================================
   Code-splitting resiliente a deploy novo — ADR §38.
   Cada tela é um chunk com hash no nome (ex.: dashboard-XXXX.js). Quando
   publicamos uma versão nova no Cloudflare Pages os hashes mudam e os arquivos
   antigos somem. Uma aba já aberta (ou um index.html em cache) ainda aponta pro
   chunk velho → o import dinâmico dá 404 ("Failed to fetch dynamically imported
   module").
   Aqui interceptamos esse 404 e recarregamos a página UMA vez pra buscar o
   index/chunks novos. Guarda por timestamp em sessionStorage evita loop de
   reload (se nem o reload resolver, o errorElement assume — ver route-error).
   =========================================================== */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_KEY = "lucraa:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

// Mensagem do import dinâmico que falha varia por navegador — cobre os casos
// conhecidos (Chrome/Firefox/Safari).
const CHUNK_ERROR =
  /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch/i;

function jaTentouRecarregar(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return true;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return false;
  } catch {
    // sessionStorage indisponível (modo restrito): não arrisca loop, deixa o
    // errorElement assumir.
    return true;
  }
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (CHUNK_ERROR.test(msg) && !jaTentouRecarregar()) {
        window.location.reload();
        // Promise que nunca resolve: a página recarrega antes de renderizar algo.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
